import { Injectable } from '@angular/core';
import {
  injectMutation,
  injectQuery,
  QueryKey,
} from '@tanstack/angular-query-experimental';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { Document, DocumentMeetingPreparationAcknowledgeDto } from '../models/document.model';
import { BaseApiService } from './base.api.service';

/**
 * Service for managing documents, including fetching document lists,
 * requesting downloads, and handling related errors.
 *
 * Provides methods to interact with the backend API for document operations.
 */
@Injectable({ providedIn: 'root' })
export class DocumentService extends BaseApiService {
  private myDocuments: QueryKey = ['myDocuments'];

  /**
   * Retrieves the list of documents for the current user.
   *
   * @returns A Signal containing the list of documents.
   */
  getListDocuments() {
    return injectQuery(() => ({
      queryKey: this.myDocuments,
      queryFn: async () => {
        try {
          return await lastValueFrom(
            this.httpClient.get<Document[]>(
              `${this.baseUrl}/api/core/documents`,
              {
                withCredentials: true,
              }
            )
          );
        } catch (error: any) {
          this.handleError(error);
          throw error;
        }
      },
    }));
  }


  requestDownloadCode = injectMutation(() => ({
    retry: false,
    mutationKey: ['requestDownloadCode'],
    mutationFn: (args: { documentId: string }) =>
      firstValueFrom(
        this.httpClient.get<void>(
          `${this.baseUrl}/api/core/documents/requestDownloadCode?documentId=${args.documentId}`,
          {
            withCredentials: true,
          }
        )
      ),
  }));

  /**
   * Mutation for requesting a download token for a specific document.
   *
   * @remarks
   * The returned object contains a key and a token for downloading the document.
   */
  askDownload = injectMutation(() => ({
    retry: false,
    mutationKey: ['askDownload'],
    mutationFn: (args: { documentId: string, token: string }) =>
      firstValueFrom(
        this.httpClient.get<{ key: string; token: string }>(
          `${this.baseUrl}/api/core/documents/askDownload?documentId=${args.documentId}&token=${args.token}`,
          {
            withCredentials: true,
          }
        )
      ),
  }));

  /**
   * Mutation pour uploader une liste de fichiers avec une application cible.
   * Envoie les fichiers et la string 'application' dans un FormData sur /core/documents/upload
   */
  uploadDocuments = injectMutation(() => ({
    mutationKey: ['uploadDocuments'],
    mutationFn: async (args: { fichiers: File[]; application: string; telBeneficiaire: string }) => {
      const formData = new FormData();
      args.fichiers.forEach(file => formData.append('Fichiers', file));
      formData.append('Application', args.application);
      formData.append('TelBeneficiaire', args.telBeneficiaire);
      return await lastValueFrom(
        this.httpClient.post(
          `${this.baseUrl}/api/core/documents`,
          formData,
          { withCredentials: true }
        )
      );
    },
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: this.myDocuments });
    }
  }));

  uploadDocumentsPrepareMeeting = injectMutation(() => ({
    mutationKey: ['uploadDocumentsPrepareMeeting'],
    mutationFn: async (args: { fichiers: File[]; idMeeting: string, themes: string[], telBeneficiaire: string }) => {
      const formData = new FormData();
      args.fichiers.forEach(file => formData.append('Fichiers', file));
      args.themes.forEach(theme => formData.append('Themes', theme));
      formData.append('IdMeeting', args.idMeeting);
      formData.append('TelBeneficiaire', args.telBeneficiaire);
      return await lastValueFrom(
        this.httpClient.post<DocumentMeetingPreparationAcknowledgeDto>(
          `${this.baseUrl}/api/core/documents/prepareMeeting`,
          formData,
          { withCredentials: true }
        )
      );
    },
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: this.myDocuments });
    }
  }));


  validateDownloadCode = injectMutation(() => ({
    mutationKey: ['validateDownloadCode'],
    mutationFn: (args: { documentId: string; verificationCode: string }) =>
      firstValueFrom(
        this.httpClient.post<string>(
          `${this.baseUrl}/api/core/documents/validateDownloadCode`,
          {
            documentId: args.documentId,
            verificationCode: args.verificationCode
          },
          { withCredentials: true }
        )
      ),
  }));

  /**
   * Supprime un document par son identifiant.
   * @param documentId L'identifiant du document à supprimer.
   */
  deleteDocument = injectMutation(() => ({
    mutationKey: ['deleteDocument'],
    mutationFn: async (args: { documentId: string }) => {
      return await lastValueFrom(
        this.httpClient.delete(
          `${this.baseUrl}/api/core/documents/${args.documentId}`,
          { withCredentials: true }
        )
      );
    },
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: this.myDocuments });
    }
  }));
}
